/**
 * The three Session cards, drawn from the studio's thread (apollo
 * src/components/chat/Thread.tsx, LinearResults.tsx): the composer with its model
 * panel open, the tool rows as the agent runs, and a result with its actions.
 * Controls follow the app (apollo src/components/chat/Thread.tsx): mode buttons,
 * +, the settings panel (aspect, style, images, then steps and seed), model, send.
 */
import type { FC } from 'react'
import {
  ArrowUpIcon, BoxIcon, CheckIcon, ChevronRightIcon, ClapIcon, ImageIcon, PlusIcon, SlidersIcon, WandIcon, WaveIcon,
  WrenchIcon,
} from './ProductFrameIcons'

const surface = 'rounded-lg border border-line bg-card shadow-[0_4px_14px_rgba(2,2,2,0.06)]'
const label = 'text-[9px] uppercase tracking-wider text-faint'
const select = 'mt-1 flex h-6 items-center justify-between rounded-md border border-line px-2 text-[10px] text-bone'

/** 1 · The composer: pick the medium, set aspect, style and count in the panel, then send. */
export const AskVisual: FC = () => (
  <div className="flex h-full flex-col justify-end gap-2">
    <div className={`${surface} p-3`}>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
        <div>
          <span className={label}>Aspect</span>
          <span className={select}>
            2:3 · 768×1152 <span className="text-faint">▾</span>
          </span>
        </div>
        <div>
          <span className={label}>Style</span>
          <span className={select}>
            Film <span className="text-faint">▾</span>
          </span>
        </div>
        <div>
          <span className={label}>Images</span>
          <span className={`${select} justify-start gap-3`}>
            <span className="text-faint">−</span>
            <span className="num">4</span>
            <span className="text-faint">+</span>
          </span>
        </div>
        <div>
          <span className={label}>Seed · locked</span>
          <span className={`${select} num`}>8812</span>
        </div>
      </div>
      <div className="mt-2.5 flex items-center gap-2 border-t border-dashed border-line pt-2.5">
        <span className={`${label} w-9`}>Steps</span>
        <span className="relative h-[3px] flex-1 rounded-full bg-line">
          <span className="absolute inset-y-0 left-0 w-[40%] rounded-full bg-bone" />
          <span className="absolute top-1/2 left-[40%] size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-lamp bg-card" />
        </span>
        <span className="num w-4 text-right text-[10px] text-lamp-ink">8</span>
      </div>
    </div>
    <div className={`${surface} rounded-xl p-2.5`}>
      <p className="px-0.5 text-[11px] text-bone">a poster for the friday show, grainy paper</p>
      <div className="mt-2.5 flex items-center gap-0.5 text-muted">
        {[WandIcon, ImageIcon, ClapIcon, WaveIcon, BoxIcon].map((Icon, i) => (
          <span key={i} className={`flex size-6 items-center justify-center rounded-md ${i === 1 ? 'bg-panel text-bone' : ''}`}>
            <Icon width={12} height={12} />
          </span>
        ))}
        <span className="flex size-6 items-center justify-center">
          <PlusIcon width={12} height={12} />
        </span>
        <span className="flex size-6 items-center justify-center rounded-md bg-lamp/20 text-lamp-ink">
          <SlidersIcon width={12} height={12} />
        </span>
        <span className="ml-auto whitespace-nowrap rounded-md px-1.5 text-[10px]">Z-Image Turbo</span>
        <span className="flex size-6 items-center justify-center rounded-full bg-bone text-card">
          <ArrowUpIcon width={11} height={11} />
        </span>
      </div>
    </div>
  </div>
)

/** Tool rows as the thread draws them: chevron, wrench, the tool's label, then a check or a spinner. */
const CALLS = [
  { label: 'Inspect canvas', done: true },
  { label: 'Generate images', done: true },
  { label: 'Update canvas', done: false },
] as const

/** 2 · The agent at work in the thread: its reply, the tools it ran, the job's status line. */
export const PlanVisual: FC = () => (
  <div className="flex h-full flex-col justify-end">
    <div className={`${surface} p-3`}>
      <p className="text-[11px] leading-snug text-bone">
        Reading poster-04 first, then four takes on your GPU. They land on the artboard as they finish.
      </p>
      <ul className="mt-2.5 flex flex-col gap-0.5">
        {CALLS.map((c) => (
          <li key={c.label} className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[10.5px] text-muted">
            <ChevronRightIcon width={9} height={9} />
            <WrenchIcon width={9} height={9} />
            {c.label}
            {c.done ? (
              <CheckIcon width={10} height={10} className="text-[#2f9e62]" />
            ) : (
              <span className="ping ml-0.5 size-1.5 rounded-full bg-lamp" aria-label="Running" />
            )}
          </li>
        ))}
      </ul>
      <div className="mt-2.5 border-t border-dashed border-line pt-2.5">
        <span className="text-[10.5px] text-muted">running · 62%</span>
        <span className="mt-1.5 block h-[2px] w-full bg-line">
          <span className="block h-full w-[62%] bg-lamp" />
        </span>
      </div>
    </div>
  </div>
)

const TURN_INTO = ['Video', '3D', 'Upscale', 'Remove background'] as const

/** 3 · A result in the thread, with the actions the app puts under every image. */
export const EditVisual: FC = () => (
  <div className="flex h-full flex-col justify-end gap-2">
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-medium text-bone">poster-04</span>
      <span className="rounded-md border border-line px-1.5 py-0.5 text-[10px] text-muted">
        v3 <span className="text-faint">▾</span>
      </span>
    </div>
    <img src="/images/medium-photography.jpg" alt="" className="h-[124px] w-full rounded-xl border border-line object-cover" />
    <div className="flex flex-wrap items-center gap-1 text-[10px]">
      <span className="rounded-md border border-line bg-card px-2 py-1 text-bone">Use as reference</span>
      <span className="px-1.5 py-1 text-muted">Open in app</span>
      <span className="px-1.5 py-1 text-muted">Artboard</span>
    </div>
    <div className="flex flex-wrap items-center gap-1 text-[10px]">
      <span className="mr-0.5 text-faint">Turn into</span>
      {TURN_INTO.map((t) => (
        <span key={t} className="rounded-md bg-panel px-2 py-1 text-bone">
          {t}
        </span>
      ))}
    </div>
  </div>
)
